/**
 * Network Status Indicator
 * Displays a visual badge in the header showing network quality.
 *
 * Quality levels:
 * - good  → green cloud icon
 * - fair  → orange signal icon
 * - poor  → red signal icon + warning banner
 * - offline → red cloud-off chip + offline banner
 *
 * Also shows the sync queue badge and notifications.
 */

import { useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Chip,
  IconButton,
  Slide,
  Snackbar,
  Tooltip,
  useTheme as useMuiTheme,
} from '@mui/material';
import {
  CloudDone as CloudDoneIcon,
  CloudOff as CloudOffIcon,
  CloudSync as CloudSyncIcon,
  SignalWifi4Bar as SignalGoodIcon,
  SignalWifi2Bar as SignalFairIcon,
  SignalWifi0Bar as SignalPoorIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { useNetwork } from '../../store/NetworkContext';
import type { NetworkQuality } from '../../hooks/useNetworkStatus';
import SyncQueuePanel from './SyncQueuePanel';

// ─── Quality → visual mapping ────────────────────────────────────────────────

function useQualityConfig(quality: NetworkQuality) {
  const theme = useMuiTheme();

  switch (quality) {
    case 'good':
      return {
        color: theme.palette.success.main,
        Icon: SignalGoodIcon,
        tooltipKey: 'network.qualityGood',
      };
    case 'fair':
      return {
        color: theme.palette.warning.main,
        Icon: SignalFairIcon,
        tooltipKey: 'network.qualityFair',
      };
    case 'poor':
      return {
        color: theme.palette.error.main,
        Icon: SignalPoorIcon,
        tooltipKey: 'network.qualityPoor',
      };
    case 'offline':
    default:
      return {
        color: theme.palette.error.main,
        Icon: CloudOffIcon,
        tooltipKey: 'network.offlineTooltip',
      };
  }
}

// ─── Component ───────────────────────────────────────────────────────────────

export function NetworkStatusIndicator() {
  const { t } = useTranslation();
  const muiTheme = useMuiTheme();
  const {
    isOnline,
    networkQuality,
    effectiveType,
    pingMs,
    pendingSyncCount,
    isProcessingQueue,
    syncNotification,
    clearSyncNotification,
  } = useNetwork();

  const [panelOpen, setPanelOpen] = useState(false);
  const showSpinner = isProcessingQueue;

  const qualityConfig = useQualityConfig(networkQuality);

  // Build tooltip text with details
  const buildTooltip = () => {
    const base = t(qualityConfig.tooltipKey);
    const parts: string[] = [base];
    if (isOnline && pingMs !== null) {
      parts.push(`Ping: ${pingMs}ms`);
    }
    if (effectiveType) {
      parts.push(effectiveType.toUpperCase());
    }
    return parts.join(' — ');
  };

  // ── Icon ────────────────────────────────────────────────────────────────────

  let indicator: React.ReactNode;

  if (!isOnline || networkQuality === 'offline') {
    // Offline: red chip
    indicator = (
      <Tooltip title={buildTooltip()}>
        <Badge
          badgeContent={pendingSyncCount > 0 ? pendingSyncCount : undefined}
          color="error"
          max={99}
          onClick={pendingSyncCount > 0 ? () => setPanelOpen(true) : undefined}
          sx={pendingSyncCount > 0 ? { cursor: 'pointer' } : undefined}
        >
          <Chip
            icon={<CloudOffIcon sx={{ fontSize: 16 }} />}
            label={t('network.offline')}
            size="small"
            sx={{
              backgroundColor: muiTheme.palette.error.main,
              color: muiTheme.palette.error.contrastText,
              fontWeight: 600,
              fontSize: '0.75rem',
              '& .MuiChip-icon': { color: muiTheme.palette.error.contrastText },
            }}
          />
        </Badge>
      </Tooltip>
    );
  } else if (showSpinner) {
    // Syncing spinner
    indicator = (
      <Tooltip title={t('network.syncInProgress', { count: pendingSyncCount })}>
        <Badge badgeContent={pendingSyncCount} color="warning" max={99}>
          <SyncIcon
            sx={{
              fontSize: 20,
              color: muiTheme.palette.warning.main,
              animation: 'spin 2s linear infinite',
              '@keyframes spin': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' },
              },
            }}
          />
        </Badge>
      </Tooltip>
    );
  } else if (pendingSyncCount > 0) {
    // Online with pending sync items
    indicator = (
      <Tooltip title={t('network.syncing', { count: pendingSyncCount })}>
        <IconButton size="small" onClick={() => setPanelOpen(true)} sx={{ p: 0.25 }}>
          <Badge badgeContent={pendingSyncCount} color="warning" max={99}>
            <CloudSyncIcon
              sx={{ fontSize: 20, color: muiTheme.palette.warning.main }}
            />
          </Badge>
        </IconButton>
      </Tooltip>
    );
  } else if (networkQuality === 'poor') {
    // Poor quality: red signal icon with chip
    indicator = (
      <Tooltip title={buildTooltip()}>
        <Chip
          icon={<SignalPoorIcon sx={{ fontSize: 16 }} />}
          label={t('network.qualityPoorShort')}
          size="small"
          sx={{
            backgroundColor: muiTheme.palette.error.main,
            color: muiTheme.palette.error.contrastText,
            fontWeight: 600,
            fontSize: '0.75rem',
            '& .MuiChip-icon': { color: muiTheme.palette.error.contrastText },
          }}
        />
      </Tooltip>
    );
  } else if (networkQuality === 'fair') {
    // Fair quality: orange signal icon
    indicator = (
      <Tooltip title={buildTooltip()}>
        <SignalFairIcon
          sx={{ fontSize: 20, color: muiTheme.palette.warning.main }}
        />
      </Tooltip>
    );
  } else {
    // Good quality: green cloud icon
    indicator = (
      <Tooltip title={buildTooltip()}>
        <CloudDoneIcon
          sx={{ fontSize: 20, color: muiTheme.palette.success.main, opacity: 0.7 }}
        />
      </Tooltip>
    );
  }

  // ── Snackbar notification ────────────────────────────────────────────────────

  const snackbarOpen = !!syncNotification;
  const snackbarMsg = syncNotification?.i18nKey
    ? t(syncNotification.i18nKey, syncNotification.i18nValues as Record<string, string | number>)
    : (syncNotification?.message ?? '');

  return (
    <>
      {indicator}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={clearSyncNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={clearSyncNotification}
          severity={syncNotification?.severity ?? 'info'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>
      <SyncQueuePanel open={panelOpen} onClose={() => setPanelOpen(false)} />
    </>
  );
}

/**
 * Network Quality Banner - displayed as a full-width bar below the header.
 * Shows when offline OR when the network quality is poor.
 */
export function OfflineBanner() {
  const { t } = useTranslation();
  const muiTheme = useMuiTheme();
  const { isOnline, networkQuality, pingMs } = useNetwork();

  const showOffline = !isOnline || networkQuality === 'offline';
  const showPoor = isOnline && networkQuality === 'poor';
  const show = showOffline || showPoor;

  return (
    <Slide direction="down" in={show} mountOnEnter unmountOnExit>
      <Box
        sx={{
          backgroundColor: showOffline
            ? muiTheme.palette.warning.dark
            : muiTheme.palette.error.light,
          color: showOffline
            ? muiTheme.palette.warning.contrastText
            : muiTheme.palette.error.contrastText,
          px: 2,
          py: 0.75,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          fontSize: '0.85rem',
          fontWeight: 500,
          zIndex: muiTheme.zIndex.appBar - 1,
        }}
      >
        {showOffline ? (
          <>
            <CloudOffIcon sx={{ fontSize: 18 }} />
            {t('network.offlineBanner')}
          </>
        ) : (
          <>
            <SignalPoorIcon sx={{ fontSize: 18 }} />
            {t('network.poorBanner', { ping: pingMs ?? '—' })}
          </>
        )}
      </Box>
    </Slide>
  );
}

export default NetworkStatusIndicator;
